import useAiChatPacedText from '@/composables/ai-chat/ai-chat-paced-text';
import useAiChatRepo from '@/composables/ai-chat/ai-chat-repo';
import useAiChatStream from '@/composables/ai-chat/ai-chat-stream';
import { AI_CHAT_FAILURES, EVENTS } from '@/lib/constants';
import { EventBus } from '@/lib/event-bus';
import { aiChatMessageOutcome, aiChatMessageRole } from 'shared/constants.json';
import { computed, ref, watch } from 'vue';
import { stopChat } from '@/api/brainstorm';

const parseTime = (value) => {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const timestamp = (message) => parseTime(message.createdAt) ?? 0;

// a retry asks the same prompt again, which moves modifiedAt but not createdAt
const lastAskedAt = (message) => parseTime(message?.modifiedAt) ?? parseTime(message?.createdAt);

// createdAt is rounded to the second, so a question and its answer can carry the same
// timestamp; uid rises with every write and settles the order the timestamp cannot.
const sortMessages = (messages) => [...messages]
  .sort((a, b) => timestamp(a) - timestamp(b) || a.uid - b.uid);

const turnTimeout = 10 * 60 * 1000;

export default function useAiChatConversation(chatId, { onChatCreated = () => {} } = {}) {
  const { selectChat, createMessage, retryPrompt, discardMessages } = useAiChatRepo();

  const sending = ref(false);
  // the question stands in for itself until the write comes back with it, so the conversation
  // shows what was asked before it shows that something is answering
  const outgoing = ref(null);
  const replacing = ref([]);

  const stored = computed(() => {
    if (chatId.value === null) {
      return [];
    }

    const chat = selectChat(chatId.value);
    if (chat === undefined) {
      return [];
    }

    return sortMessages((chat.messages ?? []).filter((m) => !replacing.value.includes(m.uid)));
  });

  const messages = computed(() => stored.value
    .filter((m) => m.outcome !== aiChatMessageOutcome.failed));

  const lastStored = computed(() => stored.value[stored.value.length - 1]);

  const awaited = computed(() => (lastStored.value?.role === aiChatMessageRole.user
    ? lastStored.value
    : null));

  const markedFailed = computed(() => lastStored.value?.outcome === aiChatMessageOutcome.failed);

  const abandoned = ref(false);

  watch(
    () => lastAskedAt(awaited.value),
    (askedAt, previous, onCleanup) => {
      if (askedAt === null) {
        abandoned.value = false;
        return;
      }

      const remaining = askedAt + turnTimeout - Date.now();
      abandoned.value = remaining <= 0;
      if (abandoned.value) {
        return;
      }

      // only a wait that runs out while the chat is open counts; reopening an old
      // unanswered chat is not a new failure
      const timer = setTimeout(() => {
        abandoned.value = true;
        EventBus.$emit(EVENTS.AI_CHAT.ANSWER_ABANDONED);
      }, remaining);
      onCleanup(() => clearTimeout(timer));
    },
    { immediate: true },
  );

  const notSent = ref(false);
  const discardTurn = () => {
    outgoing.value = null;
    notSent.value = false;
  };

  const failure = computed(() => {
    if (sending.value) {
      return null;
    }
    if (notSent.value) {
      return AI_CHAT_FAILURES.notSent;
    }
    if (markedFailed.value || (awaited.value !== null && abandoned.value)) {
      return AI_CHAT_FAILURES.unanswered;
    }
    return null;
  });

  // Derived so it survives a reload: the answer arrives on the vortex subscription, not on
  // a deadline of ours. `sending` covers only the gap before the question reaches the store.
  const pending = computed(() => sending.value
    || (awaited.value !== null && failure.value === null));

  const { streamed, connection } = useAiChatStream(chatId, pending);
  const awaitedStream = computed(() => {
    if (awaited.value === null || streamed.value?.promptId !== awaited.value.uid || streamed.value.text === '') {
      return null;
    }
    return streamed.value.text;
  });
  const streamedAnswer = useAiChatPacedText(awaitedStream);

  watch(chatId, discardTurn);

  const sendMessage = (content, attachments = []) => {
    const question = content.trim();
    if ((question === '' && attachments.length === 0) || pending.value) {
      return Promise.resolve();
    }

    discardTurn();
    sending.value = true;
    const startsChat = chatId.value === null;
    const chatEdge = startsChat ? {} : { chat: { uid: chatId.value } };
    const attachmentsAs = (shape) => (attachments.length === 0
      ? {}
      : { attachments: attachments.map(shape) });

    // the write references what was stored; the echo stands in for the message until that
    // comes back, so it needs the shape a storageObject edge is read in
    const attachmentEdge = attachmentsAs(({ fileId }) => ({ blob: fileId }));
    const echoed = attachmentsAs(({ fileId, name, getURL }) => ({ uid: fileId, fileName: name, getURL }));

    outgoing.value = { role: aiChatMessageRole.user, content: question, ...echoed };

    // a message created without a chat gets one from the backend, and the response is
    // selected after every payload ran, so the chat it was attached to comes back with it
    return createMessage({ ...chatEdge, ...attachmentEdge, role: aiChatMessageRole.user, content: question })
      .then(
        (message) => {
          outgoing.value = null;
          EventBus.$emit(EVENTS.AI_CHAT.MESSAGE_SENT, { attachmentCount: attachments.length });
          if (startsChat) {
            EventBus.$emit(EVENTS.AI_CHAT.CHAT_CREATED);
          }
          const stillOnNewChat = chatId.value === null;
          if (stillOnNewChat) {
            onChatCreated(message.chat.uid);
          }
        },
        (error) => {
          notSent.value = true;
          throw error;
        },
      )
      .finally(() => { sending.value = false; });
  };

  const lastPromptIndex = computed(() => stored.value.findLastIndex((m) => m.role === aiChatMessageRole.user));

  // a prompt the turn gave up on is retried like an answered one, just with nothing to replace
  const retryable = computed(() => !pending.value
    && lastPromptIndex.value !== -1
    && outgoing.value === null);

  const retry = () => {
    if (!retryable.value) {
      return Promise.resolve();
    }

    const prompt = stored.value[lastPromptIndex.value];
    const following = stored.value.slice(lastPromptIndex.value + 1).map((m) => m.uid);

    notSent.value = false;
    sending.value = true;
    replacing.value = following;
    return retryPrompt(prompt.uid)
      .then(
        () => discardMessages(following),
        (error) => {
          notSent.value = true;
          throw error;
        },
      )
      .finally(() => {
        replacing.value = [];
        sending.value = false;
      });
  };

  // the stop request returns before the stopped message arrives on the vortex subscription,
  // and a second click in between would have nothing left to stop
  const stopping = ref(false);
  watch(pending, (isPending) => {
    if (!isPending) {
      stopping.value = false;
    }
  });
  watch(chatId, () => { stopping.value = false; });

  const stoppable = computed(() => pending.value && !sending.value && !stopping.value);

  const stop = () => {
    if (!stoppable.value) {
      return Promise.resolve();
    }

    stopping.value = true;
    return stopChat(chatId.value).catch((error) => {
      stopping.value = false;
      throw error;
    });
  };

  return {
    messages,
    outgoing,
    pending,
    streamedAnswer,
    connection,
    failure,
    sendMessage,
    retryable,
    retry,
    stoppable,
    stop,
  };
}
